/**
 * Voice Recorder Button Component
 *
 * A button that records audio and transcribes it using OpenAI Whisper.
 * - Click to start recording
 * - Shows animated waveform during recording
 * - Click again to stop and transcribe
 * - Shows processing state and error feedback
 */

import * as React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:voice-recorder-button');
import { useOpenAISTT } from '../hooks/use-openai-stt';
import { Tooltip } from './tooltip';
import { IconButton } from './icon-button';
import { commonIcons } from './icon-button';
import { VoiceWaveform } from './voice-waveform';
import { cn } from '../utils/cn';
import type { STTRecordingState } from '../hooks/use-openai-stt';
import { toast } from '../utils/toast';

export interface VoiceRecorderButtonProps {
  /**
   * Callback when transcription is complete
   */
  onTranscriptionComplete?: (text: string) => void;

  /**
   * Optional language code (ISO-639-1)
   * @default undefined (auto-detect)
   */
  language?: string;

  /**
   * Optional context prompt for better transcription
   */
  prompt?: string;

  /**
   * Optional CSS class name
   */
  className?: string;

  /**
   * Disabled state
   */
  disabled?: boolean;
}

/**
 * Voice Recorder Button
 *
 * Simple button that handles recording and transcription workflow
 */
export type VoiceRecorderButtonHandle = {
  /** Focus the composer and begin recording (same as clicking the mic button). */
  start: () => Promise<void>;
};

export const VoiceRecorderButton = React.forwardRef<
  VoiceRecorderButtonHandle,
  VoiceRecorderButtonProps
>(function VoiceRecorderButton(
  {
    onTranscriptionComplete,
    language,
    prompt,
    className,
    disabled = false,
  },
  ref
) {
  const stt = useOpenAISTT({
    ...(language && { language }),
    ...(prompt && { prompt }),
    ...(onTranscriptionComplete && { onTranscriptionComplete }),
    onError: error => {
      logger.error('🎤 Voice recording error:', error);
      toast.error('Error recording voice. Please try again.');
    },
  });

  const startRecording = React.useCallback(async () => {
    if (disabled || stt.isProcessing || stt.isRecording) return;
    if (stt.state === 'success' || stt.state === 'error') {
      stt.reset();
    }
    await stt.startRecording();
  }, [disabled, stt]);

  React.useImperativeHandle(ref, () => ({ start: startRecording }), [startRecording]);

  // Handle button click - toggle recording
  const handleClick = async () => {
    if (stt.isRecording) {
      await stt.stopRecording();
    } else {
      await startRecording();
    }
  };

  // Determine button label and icon
  const getButtonLabel = () => {
    const labelsHash: Record<STTRecordingState, string> = {
      recording: 'Stop Recording',
      processing: 'Transcribing...',
      success: 'Record Again',
      error: 'Try Again',
      idle: 'Record Voice',
    };
    return labelsHash[stt.state] || labelsHash.idle;
  };

  // Determine button appearance
  const getButtonIcon = () => {
    const iconsHash: Record<STTRecordingState, (typeof commonIcons)[keyof typeof commonIcons]> = {
      recording: commonIcons.square,
      processing: commonIcons.loader,
      success: commonIcons.check,
      error: commonIcons.alertTriangle,
      idle: commonIcons.mic,
    };
    return iconsHash[stt.state] || iconsHash.idle;
  };

  const buttonLabel = getButtonLabel();
  const buttonIcon = getButtonIcon();
  const isButtonDisabled = disabled || stt.isProcessing;

  return (
    <div className={cn('flex flex-row-reverse items-center justify-center gap-2', className)}>
      <Tooltip content={buttonLabel}>
        <IconButton
          iconName={buttonIcon}
          size="sm"
          iconSize="md"
          onClick={handleClick}
          disabled={isButtonDisabled}
          aria-label={buttonLabel}
          className={cn(
            stt.isRecording
              ? 'text-text-secondary bg-bg-neutral-7'
              : 'text-text-secondary hover:bg-bg-neutral-7'
          )}
        />
      </Tooltip>

      {/* Show waveform when recording */}
      {stt.isRecording && <VoiceWaveform stream={stt.stream} barCount={20} height={24} />}

      {/* Show error message if any */}
      {stt.error && (
        <p className="text-danger text-xs">
          {stt.error.message || 'Error during transcription. Please try again.'}
        </p>
      )}
    </div>
  );
});
